import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { Button, Badge, Divider } from '../ui';
import type { Lot, LotStatus, Section, LotAuditLog } from '../../types/inventory';
import { STATUS_CONFIG, CATEGORY_CONFIG, fmt, formatDate } from '../../types/inventory';
import { api } from '../../lib/api';

interface TransactionContext {
  sale: any | null;
  ledgerEntries: any[];
  auditLogs: any[];
}

interface LotDetailDrawerProps {
  lot: Lot | null;
  section?: Section;
  auditLogs: LotAuditLog[];
  onClose: () => void;
  onStatusChange: (lotId: string, newStatus: LotStatus, reason?: string) => void;
  onPriceChange: (lotId: string, newPrice: number) => void;
  onLockToggle: (lotId: string, lock: boolean, reason?: string) => void;
  onAddNote: (lotId: string, note: string) => void;
  onDelete?: (lotId: string) => void;
  onReposition?: (lotId: string, data: { x_coord: number; y_coord: number; width?: number; height?: number }) => void;
  onTransactionComplete?: () => void;
}

const ALLOWED_TRANSITIONS: Record<string, LotStatus[]> = {
  AVAILABLE: ['RESERVED', 'MAINTENANCE', 'LOCKED', 'BLOCKED'],
  RESERVED: ['AVAILABLE', 'SOLD', 'LOCKED'],
  SOLD: ['MAINTENANCE'],
  MAINTENANCE: ['AVAILABLE'],
  LOCKED: ['AVAILABLE', 'RESERVED'],
  BLOCKED: ['AVAILABLE'],
};

const LotDetailDrawer: React.FC<LotDetailDrawerProps> = ({
  lot, section, auditLogs, onClose, onStatusChange, onPriceChange, onLockToggle, onAddNote, onDelete, onReposition, onTransactionComplete,
}) => {
  const [activeTab, setActiveTab] = useState<'info' | 'actions' | 'history' | 'transaction'>('info');
  const [newPrice, setNewPrice] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [noteText, setNoteText] = useState('');
  const [showPriceEdit, setShowPriceEdit] = useState(false);
  const [showPosEdit, setShowPosEdit] = useState(false);
  const [posForm, setPosForm] = useState({ x: 0, y: 0, w: 0, h: 0 });

  // Transaction state
  const [txCtx, setTxCtx] = useState<TransactionContext | null>(null);
  const [txLoading, setTxLoading] = useState(false);
  const [txError, setTxError] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [agents, setAgents] = useState<any[]>([]);
  const [reserveForm, setReserveForm] = useState({
    agentId: '', saleAmount: '', paymentType: 'INSTALLMENT', downPayment: '', installmentMonths: '12', notes: '',
  });

  // Fetch transaction context when lot changes or tab switches to transaction
  const fetchTxContext = useCallback(async () => {
    if (!lot) return;
    setTxLoading(true);
    try {
      const ctx = await api.inventory.transactions.context(lot.id);
      setTxCtx(ctx);
    } catch { setTxCtx(null); }
    setTxLoading(false);
  }, [lot]);

  useEffect(() => {
    if (activeTab === 'transaction' && lot) {
      fetchTxContext();
      api.inventory.transactions.agents().then(setAgents).catch(() => {});
    }
  }, [activeTab, lot, fetchTxContext]);

  // Client search debounce
  useEffect(() => {
    if (clientSearch.length < 2) { setClientResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const results = await api.inventory.transactions.searchClients(clientSearch);
        setClientResults(results);
      } catch { setClientResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch]);

  // Transaction handlers
  const handleReserve = async () => {
    if (!lot || !selectedClient || !reserveForm.agentId || !reserveForm.saleAmount) {
      setTxError('Select a client, agent, and enter sale amount');
      return;
    }
    setTxLoading(true);
    setTxError('');
    try {
      await api.inventory.transactions.reserve(lot.id, {
        clientId: selectedClient.id,
        agentId: reserveForm.agentId,
        saleAmount: Number(reserveForm.saleAmount),
        paymentType: reserveForm.paymentType,
        downPayment: Number(reserveForm.downPayment) || 0,
        installmentMonths: Number(reserveForm.installmentMonths) || 12,
        notes: reserveForm.notes || undefined,
      });
      fetchTxContext();
      onTransactionComplete?.();
    } catch (e: any) {
      setTxError(e.message || 'Failed to reserve');
    }
    setTxLoading(false);
  };

  const handleRelease = async () => {
    if (!lot || !window.confirm('Release this reservation? The sale will be cancelled.')) return;
    setTxLoading(true);
    try {
      await api.inventory.transactions.release(lot.id, { reason: 'Released via map' });
      fetchTxContext();
      onTransactionComplete?.();
    } catch (e: any) { setTxError(e.message || 'Failed to release'); }
    setTxLoading(false);
  };

  const handleCompleteSale = async () => {
    if (!lot || !window.confirm('Mark this lot as SOLD? This will complete the sale.')) return;
    setTxLoading(true);
    try {
      await api.inventory.transactions.completeSale(lot.id);
      fetchTxContext();
      onTransactionComplete?.();
    } catch (e: any) { setTxError(e.message || 'Failed to complete sale'); }
    setTxLoading(false);
  };

  if (!lot) return null;

  const config = STATUS_CONFIG[lot.status];
  const catConfig = section ? CATEGORY_CONFIG[section.category] : null;
  const transitions = ALLOWED_TRANSITIONS[lot.status] || [];
  const lotLogs = auditLogs.filter(l => l.lotId === lot.id);

  const handleStatusChange = (newStatus: LotStatus) => {
    onStatusChange(lot.id, newStatus, statusReason || undefined);
    setStatusReason('');
  };

  const handlePriceSave = () => {
    const p = parseFloat(newPrice);
    if (!isNaN(p) && p > 0) {
      onPriceChange(lot.id, p);
      setShowPriceEdit(false);
      setNewPrice('');
    }
  };

  const handleAddNote = () => {
    if (noteText.trim()) {
      onAddNote(lot.id, noteText.trim());
      setNoteText('');
    }
  };

  const tabs = [
    { key: 'info', label: 'Details', icon: 'solar:info-circle-bold-duotone' },
    { key: 'transaction', label: 'Transaction', icon: 'solar:hand-money-bold-duotone' },
    { key: 'actions', label: 'Actions', icon: 'solar:settings-bold-duotone' },
    { key: 'history', label: 'History', icon: 'solar:clock-circle-bold-duotone' },
  ] as const;

  return (
    <div style={{
      width: '340px', height: '100%', display: 'flex', flexDirection: 'column',
      background: 'var(--modal-bg)', backdropFilter: 'blur(24px)',
      borderLeft: '1px solid var(--border-base)',
      animation: 'fadeIn 0.2s ease',
    }}>
      {/* Header */}
      <div style={{ padding: '16px 18px', borderBottom: '1px solid rgba(148,163,184,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '8px',
              background: config.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon icon={config.icon} width={18} style={{ color: config.color }} />
            </div>
            <div>
              <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '16px', fontFamily: 'var(--font-heading)' }}>
                Lot {lot.lotNumber}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                {section?.label || lot.sectionId} · {lot.lotType?.name || 'Standard'}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'var(--bg-elevated)', border: 'none', borderRadius: '6px',
            width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'var(--text-muted)',
          }}>
            <Icon icon="solar:close-circle-bold-duotone" width={16} />
          </button>
        </div>

        {/* Quick stats */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{
            flex: 1, padding: '8px', borderRadius: '6px', background: 'var(--bg-elevated)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>{fmt(lot.price)}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Price</div>
          </div>
          <div style={{
            flex: 1, padding: '8px', borderRadius: '6px', background: config.bg, textAlign: 'center',
          }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: config.color }}>{config.label}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</div>
          </div>
          <div style={{
            flex: 1, padding: '8px', borderRadius: '6px', background: 'var(--bg-elevated)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>{lot.lotType?.capacity || 1}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Capacity</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(148,163,184,0.08)' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{
              flex: 1, padding: '10px 0', background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
              fontSize: '11px', fontWeight: 600, fontFamily: 'var(--font-body)',
              color: activeTab === t.key ? 'var(--primary-400)' : 'var(--text-muted)',
              borderBottom: activeTab === t.key ? '2px solid var(--primary-600)' : '2px solid transparent',
              transition: 'all 0.15s ease',
            }}>
            <Icon icon={t.icon} width={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
        {/* INFO TAB */}
        {activeTab === 'info' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {/* Lot Type Photo */}
            {lot.lotType?.imageUrl && (
              <>
                <div style={{
                  borderRadius: '8px', overflow: 'hidden',
                  border: '1px solid rgba(148,163,184,0.10)',
                }}>
                  <img src={lot.lotType.imageUrl} alt={lot.lotType.name}
                    style={{ width: '100%', height: '140px', objectFit: 'cover', display: 'block' }} />
                </div>
                <Divider spacing="4px" />
              </>
            )}

            {[
              ['Section', section?.label || lot.sectionId],
              ['Category', catConfig?.label || section?.category || '—'],
              ['Phase', section?.phase || '—'],
              ['Lot Type', lot.lotType?.name || '—'],
              ['Position', lot.isFreeform ? `Freeform (${Math.round(lot.x_coord)}, ${Math.round(lot.y_coord)})` : `Row ${lot.row + 1}, Col ${lot.col + 1}`],
              ['Orientation', lot.orientation],
              ['Created', formatDate(lot.createdAt)],
            ].map(([label, value], i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{label}</span>
                <span style={{ color: 'var(--text-primary)', fontSize: '12px', fontWeight: 500 }}>{value}</span>
              </div>
            ))}

            {lot.status === 'SOLD' && lot.occupantName && (
              <>
                <Divider spacing="8px" />
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Interment Info</div>
                {[
                  ['Occupant', lot.occupantName],
                  ['Interment Date', formatDate(lot.intermentDate)],
                ].map(([label, value], i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{label}</span>
                    <span style={{ color: 'var(--text-primary)', fontSize: '12px', fontWeight: 500 }}>{value}</span>
                  </div>
                ))}
              </>
            )}

            {lot.isLocked && (
              <>
                <Divider spacing="8px" />
                <div style={{
                  padding: '8px 10px', borderRadius: '6px', background: 'var(--bg-elevated)',
                  borderLeft: '3px solid var(--text-muted)',
                }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600 }}>🔒 Locked</div>
                  {lot.lockedReason && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{lot.lockedReason}</div>}
                </div>
              </>
            )}

            {lot.expiryDate && (
              <div style={{
                padding: '8px 10px', borderRadius: '6px', background: 'rgba(245,158,11,0.08)',
                borderLeft: '3px solid var(--warning)',
              }}>
                <div style={{ fontSize: '11px', color: 'var(--warning)', fontWeight: 600 }}>Reservation expires: {formatDate(lot.expiryDate)}</div>
              </div>
            )}

            {lot.notes && (
              <>
                <Divider spacing="8px" />
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Notes</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.5 }}>{lot.notes}</div>
              </>
            )}
          </div>
        )}

        {/* TRANSACTION TAB */}
        {activeTab === 'transaction' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {txLoading && (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '16px', fontSize: '12px' }}>
                <Icon icon="solar:refresh-bold-duotone" width={16} style={{ animation: 'spin 1s linear infinite', marginRight: '6px' }} />
                Loading...
              </div>
            )}

            {txError && (
              <div style={{
                padding: '8px 12px', borderRadius: '6px', background: 'rgba(239,68,68,0.10)',
                borderLeft: '3px solid #EF4444', color: '#EF4444', fontSize: '11px', fontWeight: 600,
              }}>{txError}</div>
            )}

            {/* ── AVAILABLE: Reserve Form ────────────── */}
            {lot.status === 'AVAILABLE' && !txLoading && (
              <>
                <div style={{ fontSize: '10px', color: 'var(--primary-400)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Reserve for Client
                </div>

                {/* Client search */}
                <div style={{ position: 'relative' }}>
                  {selectedClient ? (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 10px', borderRadius: '6px', background: 'rgba(13,148,136,0.10)',
                      border: '1px solid rgba(13,148,136,0.25)',
                    }}>
                      <div>
                        <div style={{ color: 'var(--text-primary)', fontSize: '12px', fontWeight: 600 }}>
                          {selectedClient.firstName} {selectedClient.lastName}
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>{selectedClient.phone}</div>
                      </div>
                      <button onClick={() => { setSelectedClient(null); setClientSearch(''); }}
                        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px' }}>
                        <Icon icon="solar:close-circle-bold-duotone" width={14} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <input placeholder="Search client (name / phone)..."
                        value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                        style={{
                          width: '100%', background: 'var(--input-bg)',
                          border: '1px solid var(--input-border)', borderRadius: '6px',
                          padding: '8px 10px', color: 'var(--input-text)', fontSize: '12px',
                          fontFamily: 'var(--font-body)', outline: 'none',
                        }} />
                      {clientResults.length > 0 && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                          background: 'var(--modal-bg)', border: '1px solid var(--border-base)',
                          borderRadius: '0 0 6px 6px', maxHeight: '160px', overflowY: 'auto',
                        }}>
                          {clientResults.map(c => (
                            <button key={c.id} onClick={() => { setSelectedClient(c); setClientResults([]); setClientSearch(''); }}
                              style={{
                                display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px',
                                background: 'none', border: 'none', borderBottom: '1px solid var(--border-base)',
                                cursor: 'pointer', color: 'var(--text-primary)', fontSize: '12px',
                                fontFamily: 'var(--font-body)',
                              }}>
                              <span style={{ fontWeight: 600 }}>{c.firstName} {c.lastName}</span>
                              <span style={{ color: 'var(--text-muted)', marginLeft: '8px' }}>{c.phone}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Agent */}
                <select value={reserveForm.agentId} onChange={e => setReserveForm(f => ({ ...f, agentId: e.target.value }))}
                  style={{
                    width: '100%', background: 'var(--input-bg)',
                    border: '1px solid var(--input-border)', borderRadius: '6px',
                    padding: '8px 10px', color: 'var(--input-text)', fontSize: '12px',
                    fontFamily: 'var(--font-body)', outline: 'none',
                  }}>
                  <option value="">Select Agent...</option>
                  {agents.map(a => <option key={a.id} value={a.id}>{a.name} ({a.role})</option>)}
                </select>

                {/* Sale amount (pre-fill with lot price) */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                  <div>
                    <label style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600 }}>Sale Amount</label>
                    <input type="number" placeholder={String(lot.price)}
                      value={reserveForm.saleAmount} onChange={e => setReserveForm(f => ({ ...f, saleAmount: e.target.value }))}
                      onFocus={() => { if (!reserveForm.saleAmount) setReserveForm(f => ({ ...f, saleAmount: String(lot.price) })); }}
                      style={{
                        width: '100%', background: 'var(--input-bg)',
                        border: '1px solid var(--input-border)', borderRadius: '6px',
                        padding: '8px 10px', color: 'var(--input-text)', fontSize: '12px',
                        fontFamily: 'var(--font-body)', outline: 'none',
                      }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600 }}>Down Payment</label>
                    <input type="number" placeholder="0"
                      value={reserveForm.downPayment} onChange={e => setReserveForm(f => ({ ...f, downPayment: e.target.value }))}
                      style={{
                        width: '100%', background: 'var(--input-bg)',
                        border: '1px solid var(--input-border)', borderRadius: '6px',
                        padding: '8px 10px', color: 'var(--input-text)', fontSize: '12px',
                        fontFamily: 'var(--font-body)', outline: 'none',
                      }} />
                  </div>
                </div>

                {/* Payment type + months */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                  <div>
                    <label style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600 }}>Payment Type</label>
                    <select value={reserveForm.paymentType} onChange={e => setReserveForm(f => ({ ...f, paymentType: e.target.value }))}
                      style={{
                        width: '100%', background: 'var(--input-bg)',
                        border: '1px solid var(--input-border)', borderRadius: '6px',
                        padding: '8px 10px', color: 'var(--input-text)', fontSize: '12px',
                        fontFamily: 'var(--font-body)', outline: 'none',
                      }}>
                      <option value="INSTALLMENT">Installment</option>
                      <option value="CASH">Cash</option>
                      <option value="SPOT_CASH">Spot Cash</option>
                    </select>
                  </div>
                  {reserveForm.paymentType === 'INSTALLMENT' && (
                    <div>
                      <label style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600 }}>Months</label>
                      <input type="number" value={reserveForm.installmentMonths}
                        onChange={e => setReserveForm(f => ({ ...f, installmentMonths: e.target.value }))}
                        style={{
                          width: '100%', background: 'var(--input-bg)',
                          border: '1px solid var(--input-border)', borderRadius: '6px',
                          padding: '8px 10px', color: 'var(--input-text)', fontSize: '12px',
                          fontFamily: 'var(--font-body)', outline: 'none',
                        }} />
                    </div>
                  )}
                </div>

                {/* Notes */}
                <input placeholder="Notes (optional)"
                  value={reserveForm.notes} onChange={e => setReserveForm(f => ({ ...f, notes: e.target.value }))}
                  style={{
                    width: '100%', background: 'var(--input-bg)',
                    border: '1px solid var(--input-border)', borderRadius: '6px',
                    padding: '8px 10px', color: 'var(--input-text)', fontSize: '12px',
                    fontFamily: 'var(--font-body)', outline: 'none',
                  }} />

                <Button variant="primary" size="sm" fullWidth icon="solar:hand-money-bold-duotone" onClick={handleReserve}>
                  Reserve Lot & Create Sale
                </Button>
              </>
            )}

            {/* ── RESERVED: Sale Details + Actions ─────── */}
            {lot.status === 'RESERVED' && txCtx?.sale && !txLoading && (
              <>
                <div style={{ fontSize: '10px', color: 'var(--warning)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Active Reservation
                </div>

                {/* Client card */}
                <div style={{
                  padding: '10px', borderRadius: '8px', background: 'rgba(245,158,11,0.08)',
                  border: '1px solid rgba(245,158,11,0.15)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <div style={{
                      width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(245,158,11,0.20)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon icon="solar:user-bold-duotone" width={14} style={{ color: '#F59E0B' }} />
                    </div>
                    <div>
                      <div style={{ color: 'var(--dark-100)', fontSize: '13px', fontWeight: 700 }}>
                        {txCtx.sale.client.firstName} {txCtx.sale.client.lastName}
                      </div>
                      <div style={{ color: 'var(--dark-500)', fontSize: '10px' }}>
                        {txCtx.sale.client.phone} · Agent: {txCtx.sale.agent.name}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sale summary */}
                {[
                  ['Sale Amount', fmt(txCtx.sale.saleAmount)],
                  ['Down Payment', fmt(txCtx.sale.downPayment)],
                  ['Total Paid', fmt(txCtx.sale.totalPaid)],
                  ['Balance', fmt(txCtx.sale.balance)],
                  ['Payment Type', txCtx.sale.paymentType],
                  ['Stage', txCtx.sale.stage],
                ].map(([label, value], i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: 'var(--dark-500)', fontSize: '11px' }}>{label}</span>
                    <span style={{ color: 'var(--dark-200)', fontSize: '12px', fontWeight: 600 }}>{value}</span>
                  </div>
                ))}

                {/* Payment progress */}
                {txCtx.sale.payments && txCtx.sale.payments.length > 0 && (
                  <>
                    <Divider spacing="4px" />
                    <div style={{ fontSize: '10px', color: 'var(--dark-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Payments ({txCtx.sale.payments.filter((p: any) => p.status === 'PAID').length}/{txCtx.sale.payments.length})
                    </div>
                    <div style={{
                      height: '6px', borderRadius: '3px', background: 'rgba(148,163,184,0.10)', overflow: 'hidden',
                    }}>
                      <div style={{
                        height: '100%', borderRadius: '3px',
                        width: `${Math.min(100, (txCtx.sale.totalPaid / txCtx.sale.saleAmount) * 100)}%`,
                        background: 'linear-gradient(90deg, #0D9488, #10B981)',
                        transition: 'width 0.3s ease',
                      }} />
                    </div>
                    <div style={{ color: 'var(--dark-400)', fontSize: '10px', textAlign: 'right' }}>
                      {Math.round((txCtx.sale.totalPaid / txCtx.sale.saleAmount) * 100)}% collected
                    </div>
                  </>
                )}

                <Divider spacing="4px" />

                {/* Documents */}
                <div style={{ fontSize: '10px', color: 'var(--dark-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Documents
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <a href={api.documents.saleContractUrl(txCtx.sale.id)} target="_blank" rel="noreferrer"
                    style={{ flex: 1, textDecoration: 'none' }}>
                    <div style={{
                      padding: '8px', borderRadius: '6px', background: 'rgba(13,148,136,0.08)',
                      border: '1px solid rgba(13,148,136,0.15)', cursor: 'pointer', textAlign: 'center',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                    }}>
                      <Icon icon="solar:document-bold-duotone" width={13} style={{ color: '#0D9488' }} />
                      <span style={{ fontSize: '10px', fontWeight: 600, color: '#0D9488' }}>Contract</span>
                    </div>
                  </a>
                  <a href={api.documents.soaUrl(txCtx.sale.id)} target="_blank" rel="noreferrer"
                    style={{ flex: 1, textDecoration: 'none' }}>
                    <div style={{
                      padding: '8px', borderRadius: '6px', background: 'rgba(99,102,241,0.08)',
                      border: '1px solid rgba(99,102,241,0.15)', cursor: 'pointer', textAlign: 'center',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                    }}>
                      <Icon icon="solar:bill-list-bold-duotone" width={13} style={{ color: '#6366F1' }} />
                      <span style={{ fontSize: '10px', fontWeight: 600, color: '#6366F1' }}>SOA</span>
                    </div>
                  </a>
                </div>

                <Divider spacing="4px" />

                {/* Actions */}
                <div style={{ display: 'flex', gap: '6px' }}>
                  <Button variant="primary" size="sm" style={{ flex: 1 }}
                    icon="solar:check-circle-bold-duotone" onClick={handleCompleteSale}>
                    Complete Sale
                  </Button>
                  <Button variant="danger" size="sm" style={{ flex: 1 }}
                    icon="solar:undo-left-bold-duotone" onClick={handleRelease}>
                    Release
                  </Button>
                </div>
              </>
            )}

            {/* ── SOLD: Sale Summary ──────────────────── */}
            {lot.status === 'SOLD' && txCtx?.sale && !txLoading && (
              <>
                <div style={{ fontSize: '10px', color: '#EF4444', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Sale Completed
                </div>

                <div style={{
                  padding: '10px', borderRadius: '8px', background: 'rgba(239,68,68,0.06)',
                  border: '1px solid rgba(239,68,68,0.12)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon icon="solar:user-bold-duotone" width={16} style={{ color: '#EF4444' }} />
                    <div>
                      <div style={{ color: 'var(--dark-100)', fontSize: '13px', fontWeight: 700 }}>
                        {txCtx.sale.client.firstName} {txCtx.sale.client.lastName}
                      </div>
                      <div style={{ color: 'var(--dark-500)', fontSize: '10px' }}>
                        {txCtx.sale.client.phone}
                      </div>
                    </div>
                  </div>
                </div>

                {[
                  ['Sale Amount', fmt(txCtx.sale.saleAmount)],
                  ['Total Paid', fmt(txCtx.sale.totalPaid)],
                  ['Balance', fmt(txCtx.sale.balance)],
                  ['Completed', txCtx.sale.completedDate ? formatDate(txCtx.sale.completedDate) : '—'],
                ].map(([label, value], i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: 'var(--dark-500)', fontSize: '11px' }}>{label}</span>
                    <span style={{ color: 'var(--dark-200)', fontSize: '12px', fontWeight: 600 }}>{value}</span>
                  </div>
                ))}

                <Divider spacing="4px" />

                {/* Documents */}
                <div style={{ fontSize: '10px', color: 'var(--dark-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Documents
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <a href={api.documents.saleContractUrl(txCtx.sale.id)} target="_blank" rel="noreferrer"
                    style={{ flex: 1, textDecoration: 'none' }}>
                    <div style={{
                      padding: '8px', borderRadius: '6px', background: 'rgba(13,148,136,0.08)',
                      border: '1px solid rgba(13,148,136,0.15)', cursor: 'pointer', textAlign: 'center',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                    }}>
                      <Icon icon="solar:document-bold-duotone" width={13} style={{ color: '#0D9488' }} />
                      <span style={{ fontSize: '10px', fontWeight: 600, color: '#0D9488' }}>Contract</span>
                    </div>
                  </a>
                  <a href={api.documents.soaUrl(txCtx.sale.id)} target="_blank" rel="noreferrer"
                    style={{ flex: 1, textDecoration: 'none' }}>
                    <div style={{
                      padding: '8px', borderRadius: '6px', background: 'rgba(99,102,241,0.08)',
                      border: '1px solid rgba(99,102,241,0.15)', cursor: 'pointer', textAlign: 'center',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                    }}>
                      <Icon icon="solar:bill-list-bold-duotone" width={13} style={{ color: '#6366F1' }} />
                      <span style={{ fontSize: '10px', fontWeight: 600, color: '#6366F1' }}>SOA</span>
                    </div>
                  </a>
                </div>
              </>
            )}

            {/* No sale for non-available statuses */}
            {!['AVAILABLE', 'RESERVED', 'SOLD'].includes(lot.status) && (
              <div style={{ color: 'var(--dark-500)', fontSize: '12px', textAlign: 'center', padding: '20px 0' }}>
                Lot is {STATUS_CONFIG[lot.status].label} — no active transaction
              </div>
            )}

            {/* RESERVED/SOLD but no sale found */}
            {(lot.status === 'RESERVED' || lot.status === 'SOLD') && !txCtx?.sale && !txLoading && (
              <div style={{ color: 'var(--dark-500)', fontSize: '12px', textAlign: 'center', padding: '20px 0' }}>
                No sale record found for this lot.
              </div>
            )}
          </div>
        )}

        {/* ACTIONS TAB */}
        {activeTab === 'actions' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {/* Status transitions */}
            <div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                Change Status
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {transitions.map(s => {
                  const sc = STATUS_CONFIG[s];
                  return (
                    <button key={s} onClick={() => handleStatusChange(s)}
                      style={{
                        padding: '6px 12px', borderRadius: '6px', border: `1px solid ${sc.color}40`,
                        background: sc.bg, color: sc.color, fontSize: '11px', fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'var(--font-body)', display: 'flex',
                        alignItems: 'center', gap: '4px', transition: 'all 0.15s',
                      }}>
                      <Icon icon={sc.icon} width={12} />
                      {sc.label}
                    </button>
                  );
                })}
              </div>
              {transitions.length > 0 && (
                <div style={{ marginTop: '6px' }}>
                  <input
                    placeholder="Reason (optional)"
                    value={statusReason}
                    onChange={e => setStatusReason(e.target.value)}
                    style={{
                      width: '100%', background: 'var(--input-bg)',
                      border: '1px solid var(--input-border)', borderRadius: '6px',
                      padding: '6px 10px', color: 'var(--input-text)', fontSize: '11px',
                      fontFamily: 'var(--font-body)', outline: 'none',
                    }}
                  />
                </div>
              )}
            </div>

            <Divider spacing="4px" />

            {/* Price edit */}
            <div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                Pricing
              </div>
              {showPriceEdit ? (
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input
                    type="number" placeholder={String(lot.price)} value={newPrice}
                    onChange={e => setNewPrice(e.target.value)}
                    style={{
                      flex: 1, background: 'var(--input-bg)',
                      border: '1px solid var(--input-border)', borderRadius: '6px',
                      padding: '6px 10px', color: 'var(--input-text)', fontSize: '12px',
                      fontFamily: 'var(--font-body)', outline: 'none',
                    }}
                  />
                  <Button variant="primary" size="sm" onClick={handlePriceSave}>Save</Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowPriceEdit(false)}>✕</Button>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 600 }}>{fmt(lot.price)}</span>
                  <Button variant="ghost" size="sm" icon="solar:pen-bold-duotone"
                    onClick={() => { setNewPrice(String(lot.price)); setShowPriceEdit(true); }}>Edit</Button>
                </div>
              )}
            </div>

            <Divider spacing="4px" />

            {/* Reposition (freeform only) */}
            {lot.isFreeform && onReposition && (
              <>
                <Divider spacing="4px" />
                <div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                    Position & Size
                  </div>
                  {showPosEdit ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                        {(['x', 'y', 'w', 'h'] as const).map(k => (
                          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: '10px', fontWeight: 600, width: '14px' }}>
                              {k === 'x' ? 'X' : k === 'y' ? 'Y' : k === 'w' ? 'W' : 'H'}
                            </span>
                            <input type="number" value={posForm[k]}
                              onChange={e => setPosForm({ ...posForm, [k]: parseInt(e.target.value) || 0 })}
                              style={{
                                flex: 1, background: 'var(--input-bg)',
                                border: '1px solid var(--input-border)', borderRadius: '4px',
                                padding: '4px 6px', color: 'var(--input-text)', fontSize: '11px',
                                fontFamily: 'var(--font-code)', outline: 'none', width: '100%',
                              }} />
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <Button variant="primary" size="sm" onClick={() => {
                          onReposition(lot.id, { x_coord: posForm.x, y_coord: posForm.y, width: posForm.w || undefined, height: posForm.h || undefined });
                          setShowPosEdit(false);
                        }}>Save</Button>
                        <Button variant="ghost" size="sm" onClick={() => setShowPosEdit(false)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '12px', fontFamily: 'var(--font-code)' }}>
                        ({Math.round(lot.x_coord)}, {Math.round(lot.y_coord)}) {lot.width && lot.height ? `${lot.width}×${lot.height}` : ''}
                      </span>
                      <Button variant="ghost" size="sm" icon="solar:pen-bold-duotone"
                        onClick={() => {
                          setPosForm({ x: Math.round(lot.x_coord), y: Math.round(lot.y_coord), w: lot.width || 0, h: lot.height || 0 });
                          setShowPosEdit(true);
                        }}>Edit</Button>
                    </div>
                  )}
                </div>
              </>
            )}

            <Divider spacing="4px" />

            {/* Lock/Unlock */}
            <div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                Lock Management
              </div>
              <Button variant={lot.isLocked ? 'secondary' : 'danger'} size="sm" fullWidth
                icon={lot.isLocked ? 'solar:lock-open-bold-duotone' : 'solar:lock-bold-duotone'}
                onClick={() => onLockToggle(lot.id, !lot.isLocked, 'Manual toggle')}>
                {lot.isLocked ? 'Unlock Lot' : 'Lock Lot'}
              </Button>
            </div>

            <Divider spacing="4px" />

            {/* Add note */}
            <div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                Add Note
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  placeholder="Type a note..."
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddNote()}
                  style={{
                    flex: 1, background: 'var(--input-bg)',
                    border: '1px solid var(--input-border)', borderRadius: '6px',
                    padding: '6px 10px', color: 'var(--input-text)', fontSize: '11px',
                    fontFamily: 'var(--font-body)', outline: 'none',
                  }}
                />
                <Button variant="primary" size="sm" onClick={handleAddNote}>Add</Button>
              </div>
            </div>

            {/* Delete lot */}
            {onDelete && lot.status !== 'SOLD' && (
              <>
                <Divider spacing="4px" />
                <div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                    Danger Zone
                  </div>
                  <Button variant="danger" size="sm" fullWidth
                    icon="solar:trash-bin-trash-bold-duotone"
                    onClick={() => { if (window.confirm(`Delete lot ${lot.lotNumber}? This cannot be undone.`)) onDelete(lot.id); }}>
                    Delete Lot
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {lotLogs.length > 0 ? lotLogs.map(log => (
              <div key={log.id} style={{
                padding: '8px 10px', borderRadius: '6px', background: 'var(--bg-elevated)',
                borderLeft: '2px solid var(--primary-600)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                  <span style={{ color: 'var(--text-primary)', fontSize: '11px', fontWeight: 600 }}>{log.action.replace('_', ' ')}</span>
                  <span style={{ color: 'var(--text-faint)', fontSize: '10px' }}>{formatDate(log.createdAt)}</span>
                </div>
                {(log.fromValue || log.toValue) && (
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    {log.fromValue && <span>{log.fromValue}</span>}
                    {log.fromValue && log.toValue && <span style={{ margin: '0 4px' }}>→</span>}
                    {log.toValue && <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{log.toValue}</span>}
                  </div>
                )}
                {log.reason && <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>{log.reason}</div>}
                {log.performedBy && <div style={{ fontSize: '10px', color: 'var(--text-faint)', marginTop: '1px' }}>by {log.performedBy}</div>}
              </div>
            )) : (
              <div style={{ color: 'var(--text-faint)', fontSize: '12px', textAlign: 'center', padding: '20px 0' }}>
                No history for this lot yet.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LotDetailDrawer;
